// 마을 지역 안내 카드(DOM). 영웅이 문 앞(약 7m)에 서면 그 문이 가는 지역의 소개·위험·보상·내 기록을 보여 준다.
// 입력을 막지 않는다(pointer-events:none). 화면 오른쪽 아래에 두어 가운데 영웅·왼쪽 조이스틱·위쪽 HUD·미니맵과 겹치지 않는다.
// 글자를 transform으로 줄이지 않는다: 화면 높이에 맞춰 너비·간격·표시 줄을 바꾼다(regionCardLayout). 본문은 실제 화면 12px 이상.
// 값이 바뀔 때만 DOM을 다시 쓴다. 문구는 regionModel(qaNote 없음)이 만든다.
import { REGIONS } from '../shared/regions';
import { REGION_IDS, type RegionId } from '../shared/levels';
import { elementRect, setPanelRect } from '../shared/panelRects';
import { Scene, type GameCommand, type GameView } from '../shared/types';
import { C, FONT, TEXT_OUTLINE, el, fmtNum } from './dom';
import { CARD_STYLE, cardBounds, cardWidth, pickCardMode, type CardMode } from './regionCardLayout';
import { doorStageLine, exitRegion, hardCardText, pickExit, regionCardText, stageLine, type RegionCardText, type StageLine, type TierTone } from './regionModel';
import { DIFFICULTY_ILVL_ADD, DIFFICULTY_LOOT } from '../shared/content/economy';
import { mobLevel } from '../shared/content/world';
import { GEM_DROP } from '../shared/content/consumables';
import { viewW, viewH } from '../shared/viewport';
import { applyFrame, regionEmblem, setUiIcon, uiIcon, type UiIcon } from './uiArt';
import { regionDataStatus, warmRegion } from '../pwa/regionData';

/** 정보 줄 아이콘(UI 아트 패스) */
const ROW_ICON: Record<string, UiIcon> = {
  'region-card-enemies': 'row-enemy', 'region-card-hazards': 'row-hazard', 'region-card-boss': 'row-boss',
  'region-card-recommend': 'row-recommend', 'region-card-reward': 'row-reward', 'region-card-record': 'row-record',
};

/** 난이도 색(글자 '입문'·'도전'도 함께 쓴다). 어두운 카드 배경 위에서 대비 7:1 이상 */
export const TIER_STYLE: Record<TierTone, { border: string; badgeBg: string; badgeText: string }> = {
  easy: { border: C.teal, badgeBg: '#12402a', badgeText: '#b8ffd0' },
  hard: { border: '#ff8a3d', badgeBg: '#5a1c08', badgeText: '#ffd2a0' },
};

/** 헬 색(하드보다 짙은 붉은 계열) */
const HELL_STYLE = { border: '#ff4a3a', badgeBg: '#4a0a0a', badgeText: '#ffb4a8' } as const;

/** 헬 문 앞 카드 문구: 하드 카드 문구 위에 헬 값(몹 Lv +20, 보상 배율, 장비 Lv +20)을 얹는다. 기록은 그 지역 헬 스테이지 합 */
function hellCardText(t: RegionCardText, hell?: GameView['hell']): RegionCardText {
  const no = REGION_IDS.indexOf(t.id) + 1;
  const L = DIFFICULTY_LOOT.hell;
  let clears = 0, stars = 0;
  if (hell) for (let i = 1; i <= 10; i++) { const id = `x${no}-s${String(i).padStart(2, '0')}`; clears += hell.stageClears(id); stars = Math.max(stars, hell.stageStars(id)); }
  return {
    ...hardCardText(t), tone: 'hard', record: `헬 클리어 ${fmtNum(clears)}회 · 최고 ${'★'.repeat(Math.max(0, Math.min(3, stars)))}${'☆'.repeat(3 - Math.max(0, Math.min(3, stars)))}`,
    recommend: `헬 몹 Lv ${mobLevel(no as 1, 'hell')}`,
    reward: `골드 ×${L.gold} · 강화석 ×${L.stones} · 장비 Lv +${DIFFICULTY_ILVL_ADD.hell}` + (GEM_DROP.regionTier[no - 1] > 0 ? ` · 보석 +${GEM_DROP.diffAdd.hell}` : ''),
  };
}

/** 헬 스테이지 줄: 헬 진행(x… id)을 stageLine('h' 접두)이 읽는 모양으로 바꿔 쓰고, 표시 글자만 '헬'로 */
function hellStageLine(hell: NonNullable<GameView['hell']>, region: RegionId): StageLine & { readonly hell?: boolean } {
  const adapted = {
    selected: (r: RegionId) => hell.selected(r), regionUnlocked: (r: RegionId) => hell.regionUnlocked(r),
    unlocked: (id: string) => hell.unlocked('x' + id.slice(1)), stageClears: (id: string) => hell.stageClears(id), stageStars: (id: string) => hell.stageStars(id),
  };
  const l = stageLine(adapted, region, 'h');
  const { hard: _h, ...rest } = l;
  return { ...rest, label: l.label.replace('하드 ', '헬 '), hell: true };
}

export class RegionCard {
  private readonly root: HTMLDivElement;
  private readonly name: HTMLDivElement;
  /** 지역 엠블럼(UI 아트 패스) */
  private readonly emblem: HTMLImageElement;
  /** 난이도 버튼 방패 그림 */
  private readonly diffIcon: HTMLImageElement;
  private readonly diffText: HTMLSpanElement;
  private readonly badge: HTMLSpanElement;
  private readonly blurb: HTMLDivElement;
  private readonly rows: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  /** 지역 데이터 받기 상태 줄(자산 패치·PWA): 받는 중일 때만 보인다. 카드 안 글줄이라 플레이 영역을 가리지 않는다 */
  private readonly dataLine: HTMLDivElement;
  private kDataText = '';
  private kDataRegion = '';
  private exit = -1;
  private shown = false;
  private key = '';
  private kScale = 0; private kW = 0; private kH = 0;
  private mode: CardMode | '' = '';
  /** 마지막으로 그린 기록(같으면 문구를 다시 만들지 않는다) */
  private kClears = -1; private kTicks = -1; private kStars = -1;
  private kRegion = '';
  /** 스테이지 줄(D3a): 고른 스테이지·종류·기록, ◀ ▶ 고르기(이 두 버튼만 입력을 받는다) */
  private readonly stage: HTMLDivElement;
  private readonly stageLabel: HTMLSpanElement;
  private readonly stageDetail: HTMLDivElement;
  private readonly prevBtn: HTMLButtonElement;
  private readonly nextBtn: HTMLButtonElement;
  private readonly mapBtn: HTMLButtonElement;
  /** 문 난이도 버튼(D3b): 노말 ⇄ 하드. 하드 1지역이 열렸을 때만 보인다 */
  private readonly diffBtn: HTMLButtonElement;
  private kHardRev = -1;
  /** 지역 난이도 색 테두리(draw가 정한다). 하드 줄이면 하드 색, 잠김이면 회색으로 덮고 노말로 돌아오면 이 값 */
  private toneBorder = '';
  private kHardDoor = false;
  private kHardDraw = -1;
  private kHardOn: boolean | null = null;
  private kHellDoor = false;
  private kHellOn: boolean | null = null;
  private kStage = '';
  private kPrRev = -1;
  private kPrCleared = -1;
  private line: (StageLine & { readonly hell?: boolean }) | null = null;
  /** 금테 틀을 입혔는지(입혔으면 두꺼워진 테두리만큼 여백을 줄인다) */
  private framed = false;

  constructor(parent: HTMLElement, private readonly view: GameView, private readonly send: (c: GameCommand) => void = () => {}, private readonly onOpenMap: (region: RegionId) => void = () => {}) {
    this.root = el('div', 'position:absolute;right:8px;bottom:12px;width:262px;box-sizing:border-box;display:none;pointer-events:none;transform-origin:100% 100%;' +
      'padding:var(--rc-pad);border:2px solid ' + C.teal + ';border-radius:12px;color:#e6ecff;font-family:' + FONT + ';' +
      'background:linear-gradient(180deg,rgba(26,34,56,.96),rgba(16,22,42,.96));box-shadow:0 4px 14px rgba(0,0,0,.55);', parent);
    // 금테 카드 틀(UI 아트 패스, 9-slice). 그림을 받았을 때만 입히고 배치를 다시 잰다(못 받으면 위 2px 테두리·여백 그대로)
    applyFrame(this.root, 'frame-card', 10, 9, () => { this.framed = true; this.relayout(); });
    this.root.dataset.testid = 'region-card';
    setPanelRect('region-card', elementRect(this.root));
    this.root.setAttribute('role', 'status');
    const head = el('div', 'display:flex;align-items:center;justify-content:space-between;gap:4px;', this.root);
    const title = el('div', 'display:flex;align-items:center;gap:4px;min-width:0;', head);
    this.emblem = uiIcon('emblem-r1', 30, title, 'margin:-8px 0 -8px -4px;');
    this.name = el('div', 'min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font:900 var(--rc-name) ' + FONT + ';color:#fff;letter-spacing:.5px;' + TEXT_OUTLINE, title, '');
    this.name.dataset.testid = 'region-card-name';
    this.badge = el('span', 'flex:0 0 auto;padding:2px 10px;border-radius:999px;border:1.5px solid;font:900 13px ' + FONT + ';white-space:nowrap;', head, '');
    this.badge.dataset.testid = 'region-card-tier';
    this.diffBtn = el('button', 'pointer-events:auto;cursor:pointer;flex:0 0 auto;height:26px;padding:0 7px 0 2px;border-radius:999px;border:1.5px solid #ff8a3d;background:#1a2238;color:#ffd2a0;font:900 13px ' + FONT + ';display:none;align-items:center;gap:1px;', head) as HTMLButtonElement;
    this.diffIcon = uiIcon('diff-normal', 24, this.diffBtn, 'margin:-6px 0;');
    this.diffText = el('span', '', this.diffBtn, '노말');
    this.diffBtn.dataset.testid = 'difficulty-toggle';
    this.diffBtn.setAttribute('aria-label', '난이도 바꾸기');
    this.diffBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.diffBtn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      const h = this.view.hard, x = this.view.hell;
      if (!h || !h.available) return;
      // 노말 → 하드 → (헬이 열렸으면) 헬 → 노말
      if (x && x.available && x.on) this.send({ type: 'setDifficulty', hard: false });
      else if (h.on) this.send(x && x.available ? { type: 'setDifficulty', hard: true, hell: true } : { type: 'setDifficulty', hard: false });
      else this.send({ type: 'setDifficulty', hard: true });
    });
    this.stage = el('div', 'margin-top:5px;padding:3px 6px;border-radius:8px;background:rgba(0,0,0,.32);', this.root);
    this.stage.dataset.testid = 'region-card-stage';
    const sr = el('div', 'display:flex;align-items:center;gap:6px;', this.stage);
    const arrow = 'pointer-events:auto;cursor:pointer;flex:0 0 auto;min-width:34px;height:26px;border-radius:8px;border:1.5px solid ' + C.gold + ';background:#1a2238;color:' + C.gold + ';font:900 15px ' + FONT + ';';
    this.prevBtn = el('button', arrow, sr, '◀') as HTMLButtonElement;
    this.prevBtn.dataset.testid = 'stage-prev';
    this.prevBtn.setAttribute('aria-label', '이전 스테이지');
    this.stageLabel = el('span', 'flex:1 1 auto;min-width:0;text-align:center;word-break:keep-all;font:900 15px ' + FONT + ';color:#fff;' + TEXT_OUTLINE, sr, '');
    this.stageLabel.dataset.testid = 'stage-label';
    this.nextBtn = el('button', arrow, sr, '▶') as HTMLButtonElement;
    this.nextBtn.dataset.testid = 'stage-next';
    this.nextBtn.setAttribute('aria-label', '다음 스테이지');
    this.stageDetail = el('div', 'margin-top:3px;font:600 12px/1.3 ' + FONT + ';color:#ffe7a8;text-align:center;word-break:keep-all;', this.stage, '');
    // 「지도 열기」(S3): 전체 화면 스테이지 지도. 잠긴 지역에서는 숨긴다
    this.mapBtn = el('button', 'pointer-events:auto;cursor:pointer;display:block;width:100%;min-height:44px;margin-top:4px;border-radius:8px;border:1.5px solid ' + C.gold + ';background:linear-gradient(#26335a,#1a2238);color:' + C.gold + ';font:900 14px ' + FONT + ';', this.stage, '지도 열기') as HTMLButtonElement;
    this.mapBtn.dataset.testid = 'stage-map-open';
    this.mapBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.mapBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const l = this.line, region = this.kRegion;
      if (!l || l.locked || !region) return;
      this.onOpenMap(region as RegionId);
      this.send({ type: 'openStageMap' });
    });
    const pick = (d: number) => (ev: Event) => {
      ev.stopPropagation();
      const l = this.line, region = this.kRegion;
      if (!l || l.locked || !region) return;
      const to = l.index + d;
      if (to < 1 || to > l.max) return;
      this.send({ type: 'selectStage', region: region as never, index: to, ...(l.hell ? { hell: true } : l.hard ? { hard: true } : {}) });
    };
    this.prevBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.nextBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.prevBtn.addEventListener('click', pick(-1));
    this.nextBtn.addEventListener('click', pick(1));
    this.blurb = el('div', 'margin-top:5px;font:500 var(--rc-blurb)/1.4 ' + FONT + ';color:#e6ecff;word-break:keep-all;', this.root, '');
    this.rows = el('div', 'margin-top:var(--rc-rows-top);display:flex;flex-direction:column;gap:var(--rc-gap);', this.root);
    this.hint = el('div', 'margin-top:var(--rc-hint-top);padding-top:var(--rc-hint-pad);border-top:1px solid rgba(242,193,78,.35);font:800 var(--rc-blurb) ' + FONT + ';color:' + C.gold + ';', this.root, '');
    this.dataLine = el('div', 'display:none;margin-top:4px;font:700 12px ' + FONT + ';color:#9fd6ff;', this.root, '');
    this.dataLine.dataset.testid = 'region-card-data';
  }

  /** 카드가 지금 보이는지(테스트·QA 확인용) */
  get visible(): boolean { return this.shown; }

  private row(label: string, value: string | readonly string[], testid: string, extra = false): void {
    const r = el('div', 'display:flex;gap:8px;align-items:baseline;', this.rows);
    r.dataset.testid = testid;
    if (extra) r.dataset.extra = '1';
    // 줄 머리: 아이콘 그림(UI 아트 패스)이 있으면 그림만 보이고 글자는 화면 읽기용으로 남긴다 — 값 칸이 넓어져 줄이 접히지 않는다.
    // 그림을 못 받으면 글자 머리(기존 46px)로 돌아간다
    const ic = ROW_ICON[testid];
    const lab = el('div', 'flex:0 0 46px;display:flex;align-items:center;font:800 12px ' + FONT + ';color:#ffe7a8;white-space:nowrap;', r);
    const txt = el('span', '', lab, label);
    if (ic) {
      const im = uiIcon(ic, 20, lab, 'margin:-2px 0 0;');
      // 그림이 줄 높이를 키우지 않게: 머리 칸 높이를 첫 줄 높이로 묶고 줄은 위쪽 정렬
      lab.style.flexBasis = '22px'; lab.style.height = '17px'; lab.style.overflow = 'visible'; lab.style.alignItems = 'flex-start';
      r.style.alignItems = 'flex-start';
      txt.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);';
      im.title = label;
      // 글자 머리로 돌아가면 줄 높이가 바뀔 수 있으니 배치를 다시 잰다(PR #177 리뷰 P2: 늘어난 카드가 플레이 영역에 닿지 않게)
      im.addEventListener('error', () => { lab.style.flexBasis = '46px'; lab.style.height = ''; r.style.alignItems = 'baseline'; txt.style.cssText = ''; this.relayout(); }, { once: true });
    }
    const box = el('div', 'flex:1 1 auto;min-width:0;font:500 var(--rc-body)/1.35 ' + FONT + ';color:#fff;word-break:keep-all;', r);
    if (typeof value === 'string') box.textContent = value;
    else for (const line of value) el('div', '', box, line);
  }

  private draw(t: RegionCardText, hell = false): void {
    const st = hell ? HELL_STYLE : TIER_STYLE[t.tone];
    this.toneBorder = st.border;
    this.root.style.borderColor = st.border;
    this.name.textContent = t.name;
    setUiIcon(this.emblem, regionEmblem(t.id));
    this.badge.textContent = t.tier;
    this.badge.style.cssText += 'background:' + st.badgeBg + ';color:' + st.badgeText + ';border-color:' + st.border + ';';
    this.blurb.textContent = t.blurb;
    this.rows.textContent = '';
    this.row('주요 적', t.enemies, 'region-card-enemies', true);
    this.row('위험', t.hazards, 'region-card-hazards');
    this.row('보스', t.boss, 'region-card-boss', true);
    this.row('권장', t.recommend, 'region-card-recommend');
    this.row('보상', t.reward, 'region-card-reward');
    this.row('내 기록', t.record, 'region-card-record');
    this.hint.textContent = t.hint;
  }

  /** u: 디자인 해상도 배율 */
  update(u: number): void {
    const v = this.view;
    const vv = v.village;
    const on = v.flow.scene === Scene.Village && !v.flow.paused && vv.menuNpc < 0 && !vv.inventoryOpen && !vv.stageMapOpen && !vv.questOpen;
    if (!on) { this.hide(); return; }
    this.exit = pickExit(v.map.exits, v.hero.x, v.hero.z, this.exit);
    const region = exitRegion(v.map.exits, this.exit);
    if (!region) { this.hide(); return; }
    const info = REGIONS[region];
    const rec = v.records[region];
    const clears = rec ? rec.clears : 0, ticks = rec ? rec.bestClearTicks : 0, stars = rec ? rec.bestStars : 0;
    // 하드 문(D3b): 문 난이도가 하드이고 그 지역 하드가 열렸으면 테두리·권장·보상 줄을 하드 값으로
    const hellDoor = !!v.hell && v.hell.on && v.hell.available && v.hell.regionUnlocked(region);
    const hardDoor = !hellDoor && !!v.hard && v.hard.on && v.hard.available && v.hard.regionUnlocked(region);
    const hardRev = hellDoor && v.hell ? 1000000 + v.hell.revision : hardDoor && v.hard ? v.hard.revision : -1;
    let changed = false;
    if (region !== this.kRegion || clears !== this.kClears || ticks !== this.kTicks || stars !== this.kStars || hardDoor !== this.kHardDoor || hellDoor !== this.kHellDoor || hardRev !== this.kHardDraw) {
      this.kHellDoor = hellDoor;
      this.kRegion = region; this.kClears = clears; this.kTicks = ticks; this.kStars = stars; this.kHardDoor = hardDoor; this.kHardDraw = hardRev;
      changed = true;
    }
    if (!this.shown) { this.shown = true; this.root.style.display = 'block'; changed = true; }
    if (changed) { const t = regionCardText(info, rec); this.draw(hellDoor ? hellCardText(t, v.hell) : hardDoor ? hardCardText(t, v.hard) : t, hellDoor); this.mode = ''; this.kStage = ''; }
    // 스테이지 줄(D3a): 지역·진행이 바뀔 때만 다시 계산하고 쓴다(프레임마다 문자열을 만들지 않는다)
    const pr = v.progress, prRev = pr ? pr.revision : 0, prCleared = pr ? pr.cleared : 0;
    const hd = v.hard, hdRev = (hd ? hd.revision : 0) + (v.hell ? v.hell.revision * 1000 : 0), hdOn = hd ? hd.on && hd.available : false;
    const hxOn = hellDoor;
    if (region !== this.kStage || prRev !== this.kPrRev || prCleared !== this.kPrCleared || hdRev !== this.kHardRev || hdOn !== this.kHardOn || hxOn !== this.kHellOn) {
      this.kHellOn = hxOn;
      this.kStage = region; this.kPrRev = prRev; this.kPrCleared = prCleared; this.kHardRev = hdRev; this.kHardOn = hdOn;
      // 난이도 버튼(D3b): 하드 1지역이 열렸을 때만, 지금 문 난이도를 글자로
      this.diffBtn.style.display = hd && hd.available ? 'inline-flex' : 'none';
      // 난이도 버튼이 보여도 지역 등급 배지(입문·도전)는 남긴다(PR #177 리뷰 P2) — 지역 이름이 덜 잘리게 작은 배지로
      const compact = !!hd && hd.available;
      this.badge.style.padding = compact ? '1px 5px' : '2px 10px';
      this.badge.style.fontSize = compact ? '12px' : '13px';
      this.name.style.letterSpacing = compact ? '0' : '.5px';
      this.diffText.textContent = hxOn ? '헬' : hdOn ? '하드' : '노말';
      setUiIcon(this.diffIcon, hdOn ? 'diff-hard' : 'diff-normal');
      this.diffBtn.style.background = hxOn ? HELL_STYLE.badgeBg : hdOn ? '#5a1c08' : '#1a2238';
      this.diffBtn.style.borderColor = hxOn ? HELL_STYLE.border : '#ff8a3d';
      this.diffBtn.style.color = hxOn ? HELL_STYLE.badgeText : '#ffd2a0';
      this.diffBtn.setAttribute('aria-pressed', hdOn ? 'true' : 'false');
      const line: StageLine & { readonly hell?: boolean } = hxOn && v.hell ? hellStageLine(v.hell, region) : doorStageLine(pr, hd, region);
      this.line = line;
      this.stageLabel.textContent = line.locked ? '잠긴 지역' : `스테이지 ${line.label} · ${line.kind}`;
      this.stageDetail.textContent = line.detail;
      this.prevBtn.style.visibility = line.canPrev ? 'visible' : 'hidden';
      this.nextBtn.style.visibility = line.canNext ? 'visible' : 'hidden';
      this.mapBtn.style.display = line.locked ? 'none' : 'block';
      this.root.style.borderColor = line.locked ? '#6a7088' : line.hell ? HELL_STYLE.border : line.hard ? TIER_STYLE.hard.border : this.toneBorder || this.root.style.borderColor;
      this.hint.textContent = line.locked ? '아직 들어갈 수 없습니다' : `문으로 걸어 들어가면 ${line.label} 입장`;
    }
    // 지역 데이터 받기(자산 패치): 이 문 앞에 서면 그 지역을 먼저 받고, 받는 중이면 퍼센트를 보인다
    if (region !== this.kDataRegion) { this.kDataRegion = region; warmRegion(region, this.line?.locked === true); }
    const ds = regionDataStatus(region);
    const dataText = ds.state === 'running' || ds.state === 'queued' ? '지역 데이터 받는 중 ' + ds.pct + '%' : ds.state === 'failed' ? '지역 데이터를 받지 못했어요 · 들어가면 다시 받아요' : '';
    if (dataText !== this.kDataText) { this.kDataText = dataText; this.dataLine.textContent = dataText; this.dataLine.style.display = dataText ? 'block' : 'none'; this.kScale = -1; }
    const W = viewW(), H = viewH();
    if (changed || u !== this.kScale || W !== this.kW || H !== this.kH) {
      this.kScale = u; this.kW = W; this.kH = H;
      // 넉넉한 배치부터 재어 미니맵 아래 영역에 들어가는 첫 배치를 쓴다(글자는 줄이지 않는다)
      const b = cardBounds(W, H, u);
      const mode = pickCardMode(b.availH, (m) => { this.applyMode(m, b.availW); return this.root.offsetHeight; });
      this.applyMode(mode, b.availW);
      this.root.style.transform = b.zoom > 1.001 ? 'scale(' + b.zoom.toFixed(3) + ')' : 'none';
    }
  }

  /** 다음 update에서 배치를 다시 잰다(그림 받기 결과처럼 비동기로 카드 크기가 바뀔 때) */
  private relayout(): void { this.kScale = Number.NaN; }

  /** 배치를 적용한다(너비·간격·글자 크기·표시 줄). 글자 크기는 모두 12px 이상 */
  private applyMode(mode: CardMode, availW: number): void {
    const c = CARD_STYLE[mode], s = this.root.style;
    s.width = cardWidth(mode, availW).toFixed(1) + 'px';
    // 금테 틀(테두리 9px)은 기존 테두리(2px)보다 7px 두꺼우므로 여백을 8px 줄여(0 하한) 안쪽 여백을 그만큼 줄여 배치 높이를 그대로 둔다(줄이 접히지 않게)
    const padCut = this.framed ? 8 : 0;
    s.setProperty('--rc-pad', c.pad.split(' ').map((v) => Math.max(0, parseFloat(v) - padCut) + 'px').join(' '));
    s.setProperty('--rc-name', c.nameSize + 'px');
    s.setProperty('--rc-blurb', c.blurbSize + 'px');
    s.setProperty('--rc-body', c.bodySize + 'px');
    s.setProperty('--rc-gap', c.rowGap + 'px');
    s.setProperty('--rc-rows-top', c.rowsTop + 'px');
    s.setProperty('--rc-hint-top', c.hintTop + 'px');
    s.setProperty('--rc-hint-pad', c.hintPad + 'px');
    this.blurb.style.display = c.blurb ? 'block' : 'none';
    // 가장자리 띠 마지막 배치(band)는 정보 줄을 접는다(PR #160 리뷰: 카드가 플레이 영역에 닿지 않게)
    this.rows.style.display = c.rows ? 'flex' : 'none';
    // 스테이지 줄 설명(D3a)도 좁은 배치에서는 접는다(스테이지 번호·종류는 항상 보인다)
    this.stageDetail.style.display = c.blurb ? 'block' : 'none';
    const rows = this.rows.children;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i] as HTMLElement;
      if (r.dataset.extra === '1') r.style.display = c.extra ? 'flex' : 'none';
    }
    this.mode = mode;
  }

  private hide(): void {
    this.exit = -1;
    this.kRegion = '';
    this.kStage = ''; this.line = null;
    if (!this.shown) return;
    this.shown = false;
    this.root.style.display = 'none';
  }
}
